import React, { useEffect, useMemo, useState } from "react";
import ScrollToTop from "../ScrollToTop";
import AdminSidebar from "../../Component/AdminSidebar";
import OrderPipeline from "../../components/Orders/OrderPipeline";
import OrderModeTabs from "../../components/Orders/OrderModeTabs";
import OrderDocumentBar from "../../components/Orders/OrderDocumentBar";
import OrderSAV from "../../components/Orders/OrderSAV";

import {
  advanceOrderWorkflow,
  applyOrderCancellation,
  applyOrderWorkflowChange,
  dispatchStatusOptions,
  formatOrderAmount,
  formatOrderDate,
  getNextOrderWorkflowAction,
  getOrderLifecycleBadgeClass,
  getOrderLifecycleKey,
  getOrderLifecycleLabel,
  getOrderSettlementLabel,
  getRefundModeLabel,
  isWeightedOrderItem,
  loadOrders,
  orderStatusOptions,
  paymentStatusOptions,
  printCashierReceipt,
  printOrderCreditNote,
  printOrderDeliveryNote,
  printOrderInvoice,
  printBonDeCommande,
  recalculateOrderTotals,
  refundModeOptions,
  saveOrders,
  buildInvoicePrintHtml,
  getOrderModeLabel,
  applyPartialReturn
} from "../../data/orders";
import { loadOrderLotConsumption, reintegrateCancelledOrderStock } from "../../data/inventory";
import { loadDrivers } from "../../data/drivers";
import {
  loadMessageTemplates,
  saveMessageTemplate,
  deleteMessageTemplate
} from "../../data/messageTemplates";
import B2BPaymentModal from "../../components/Orders/B2BPaymentModal";
import { createPayment, getOrderFinancials } from "../../data/payments";

const MyAcconutPaymentMethod = () => {
  const [loaderStatus, setLoaderStatus] = useState(true);
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [orderLotConsumption, setOrderLotConsumption] = useState({});
  const [activeMode, setActiveMode] = useState("all");
  const [orderSearch, setOrderSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [cancellationForm, setCancellationForm] = useState({
    reason: "",
    refundMode: "none",
    refundAmount: "",
    stockReturned: false,
  });
  const [activeTab, setActiveTab] = useState("commandes");
  const [driversList, setDriversList] = useState([]);
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [templateForm, setTemplateForm] = useState({ id: "", nom: "", message: "" });
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchOrders = async () => {
      const savedOrders = await loadOrders();
      if (!isMounted) return;
      setOrders(savedOrders);
      setSelectedId(savedOrders[0]?.id || "");
      setLoaderStatus(false);
      loadDrivers().then(setDriversList);
      loadMessageTemplates().then(setMessageTemplates);
    };
    fetchOrders();
    return () => { isMounted = false; };
  }, []);

  const counts = useMemo(() => {
    const counts = { in_store: 0, takeaway: 0, delivery: 0, all: orders.length };
    orders.forEach(o => {
      if (counts[o.orderMode] !== undefined) counts[o.orderMode]++;
    });
    return counts;
  }, [orders]);

  const visibleOrders = useMemo(() => {
    let result = activeMode === 'all' ? orders : orders.filter(o => o.orderMode === activeMode);

    if (orderSearch.trim()) {
      const q = orderSearch.trim().toLowerCase();
      result = result.filter((order) => {
        const orderNum = String(order.orderNumber || order.id || "").toLowerCase();
        const client = String(order.client || order.clientName || "").toLowerCase();
        const payment = String(order.paymentMethod || "").toLowerCase();
        return orderNum.includes(q) || client.includes(q) || payment.includes(q);
      });
    }

    if (dateFrom) result = result.filter(o => (o.date || o.createdAt || "").slice(0, 10) >= dateFrom);
    if (dateTo) result = result.filter(o => (o.date || o.createdAt || "").slice(0, 10) <= dateTo);

    return result;
  }, [activeMode, orders, orderSearch, dateFrom, dateTo]);

  const selectedOrder = useMemo(
    () => orders.find((order) => order.id === selectedId) || null,
    [orders, selectedId]
  );

  useEffect(() => {
    if (!selectedOrder) return;
    setCancellationForm({
      reason: selectedOrder.cancellationReason || "",
      refundMode: selectedOrder.refundMode || "none",
      refundAmount: selectedOrder.refundAmount > 0 ? String(selectedOrder.refundAmount) : String(selectedOrder.amount || 0),
      stockReturned: Boolean(selectedOrder.stockReturned),
    });
  }, [selectedOrder]);

  useEffect(() => {
    let isMounted = true;
    const fetchLotConsumption = async () => {
      if (!selectedId) { setOrderLotConsumption({}); return; }
      const usage = await loadOrderLotConsumption(selectedId);
      if (isMounted) setOrderLotConsumption(usage);
    };
    fetchLotConsumption();
    return () => { isMounted = false; };
  }, [selectedId, orders]);

  useEffect(() => {
    if (selectedId && !visibleOrders.some((order) => order.id === selectedId)) {
      setSelectedId(visibleOrders[0]?.id || "");
    }
  }, [selectedId, visibleOrders]);

  const updateOrders = async (nextOrders) => {
    const saved = await saveOrders(nextOrders);
    setOrders(saved);
  };

  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const handleApplyPayment = async (paymentData) => {
    if (!selectedOrder) return;
    
    // Process payments into the new central ledger
    if (paymentData.type === "DIFFERE") {
      let nextOrder = { ...selectedOrder, paymentDiffere: { invoice: paymentData.invoice, ref: paymentData.ref } };
      await updateOrders(orders.map((o) => (o.id === selectedOrder.id ? nextOrder : o)));
    } else if (paymentData.type === "N_FOIS") {
      paymentData.installments.forEach(inst => {
        createPayment({
          orderId: selectedOrder.id,
          amount: inst.amount,
          method: inst.method || "CHEQUE",
          reference: inst.ref || "",
          date: inst.date || new Date().toISOString(),
          status: "PENDING" // Post-dated checks start as pending
        });
      });
      // trigger re-render
      await updateOrders([...orders]); 
    } else {
      createPayment({
        orderId: selectedOrder.id,
        amount: paymentData.amount,
        method: paymentData.method || paymentData.type || "CASH",
        date: new Date().toISOString(),
        status: "CLEARED" // Direct payment clears immediately
      });
      // trigger re-render
      await updateOrders([...orders]); 
    }
    
    setShowPaymentModal(false);
  };

  const updateOrderField = async (id, field, value) => {
    await updateOrders(orders.map((order) => (order.id === id ? applyOrderWorkflowChange(order, field, value) : order)));
  };



  const handleAddSAVMessage = async (text) => {
    if (!selectedOrder) return;
    const newMsg = { text, date: new Date().toISOString(), author: "Staff" };
    const savMessages = [...(selectedOrder.savMessages || []), newMsg];
    await updateOrders(orders.map(o => o.id === selectedOrder.id ? { ...o, savMessages } : o));
  };

  const applyCancellationToSelectedOrder = async () => {
    if (!selectedOrder) return;
    const nextOrder = applyOrderCancellation(selectedOrder, {
      reason: cancellationForm.reason,
      refundMode: cancellationForm.refundMode,
      refundAmount: cancellationForm.refundAmount,
      stockReturned: cancellationForm.stockReturned,
    });
    if (!selectedOrder.stockReturned && nextOrder.stockReturned) {
      await reintegrateCancelledOrderStock(selectedOrder, nextOrder);
    }
    await updateOrders(orders.map((order) => (order.id === selectedOrder.id ? nextOrder : order)));
  };

  const handleEmailInvoice = async () => {
    if (!selectedOrder) return;
    const invoiceHtml = buildInvoicePrintHtml(selectedOrder);
    const plainText = invoiceHtml.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/&#8364;/g, "EUR").replace(/\s+/g, " ").trim();
    const subject = encodeURIComponent(`Facture ${selectedOrder.invoiceNumber || selectedOrder.orderNumber} - Almoune`);
    const body = encodeURIComponent(plainText.slice(0, 1800));
    const email = encodeURIComponent(selectedOrder.email || "");
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, "_blank");
  };

  const handleReturnItem = async (item) => {
    if (!selectedOrder) return;
    if (!window.confirm(`Voulez-vous vraiment retourner l'article ${item.name} ?`)) return;

    const returnedItem = {
      productId: item.productId || item.id,
      quantity: item.quantity,
      price: item.unitPrice,
      lineTotal: item.lineTotal,
      returnReason: "Retour depuis gestion des commandes"
    };

    const updatedOrder = await applyPartialReturn(selectedOrder.id || selectedOrder.orderNumber, [returnedItem]);
    
    if (updatedOrder) {
      await updateOrders(orders.map((o) => (o.id === updatedOrder.id ? updatedOrder : o)));
      alert(`Article ${item.name} retourné avec succès.`);
    } else {
      alert("Erreur lors du retour de l'article.");
    }
  };

  const navigateOrder = (direction) => {
    const idx = visibleOrders.findIndex(o => o.id === selectedId);
    if (idx === -1) return;
    const nextIdx = idx + direction;
    if (nextIdx >= 0 && nextIdx < visibleOrders.length) {
      setSelectedId(visibleOrders[nextIdx].id);
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({ id: "", nom: "", message: "" });
    setEditingTemplateId(null);
  };

  const startEditTemplate = (template) => {
    setTemplateForm({ id: template.id, nom: template.nom, message: template.message });
    setEditingTemplateId(template.id);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!templateForm.nom.trim() || !templateForm.message.trim()) return;
    await saveMessageTemplate({
      id: templateForm.id || undefined,
      nom: templateForm.nom.trim(),
      message: templateForm.message.trim(),
    });
    setMessageTemplates(await loadMessageTemplates());
    resetTemplateForm();
  };

  const handleDeleteTemplate = async (template) => {
    if (!window.confirm(`Supprimer le message prédéfini "${template.nom}" ?`)) return;
    await deleteMessageTemplate(template.id);
    setMessageTemplates(await loadMessageTemplates());
    if (editingTemplateId === template.id) resetTemplateForm();
  };

  const handleSendTemplateEmail = (templateId, targetOrder) => {
    const template = messageTemplates.find((t) => Number(t.id) === Number(templateId));
    if (!template) return;
    const order = targetOrder || selectedOrder;
    if (!order) return;
    const subject = encodeURIComponent(
      template.nom === "Retard stock"
        ? `Commande ${order.orderNumber || order.id} - Retard de livraison`
        : template.nom
    );
    const body = encodeURIComponent(template.message);
    const email = encodeURIComponent(order.email || order.clientEmail || "");
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, "_blank");
  };

  if (loaderStatus) {
    return (
      <div><ScrollToTop /><section><div className="container"><div className="row"><AdminSidebar activePath="/MyAcconutPaymentMethod" /><div className="col-lg-9 col-md-8 col-12"><div className="p-6 p-lg-10"><div className="alert alert-light border mb-0">Chargement des commandes...</div></div></div></div></div></section></div>
    );
  }

  const lifecycleKey = selectedOrder ? getOrderLifecycleKey(selectedOrder) : 'pending';

  return (
    <div>
      <ScrollToTop />
      <section className="admin-section">
        <div className="container">
          <div className="row">
            <AdminSidebar activePath="/MyAcconutPaymentMethod" />
            <div className="col-lg-9 col-md-8 col-12">
              <div className="p-6 p-lg-10">
                <div className="d-flex justify-content-between align-items-center mb-6 flex-wrap gap-2">
                  <div>
                    <h2 className="mb-1">Gestion des commandes</h2>
                    <p className="text-muted mb-0">Suivez les statuts, le paiement et les notes de preparation.</p>
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    <a href="/MyAccountCashier" className="btn btn-primary" style={{backgroundColor: '#e67e22', borderColor: '#e67e22'}}>
                      Aller à la caisse (POS)
                    </a>
                  </div>
                </div>

                <div className="admin-tabs">
                  {[
                    { key: "commandes", label: "Commandes" },
                    { key: "detail", label: "Detail commande" },
                    { key: "messages", label: "Messages prédéfinis" },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      className={`admin-tab-btn${activeTab === tab.key ? " active" : ""}`}
                      onClick={() => setActiveTab(tab.key)}
                      disabled={tab.key === "detail" && !selectedOrder}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                {activeTab === "commandes" && (
                <>
                <OrderModeTabs activeMode={activeMode} onModeChange={setActiveMode} counts={counts} />

                <div className="card border-0 shadow-sm mb-8">
                  <div className="card-body p-4">
                    <div className="row g-2 mb-3">
                      <div className="col-md-4">
                        <input type="search" className="form-control" placeholder="Rechercher numero, client..." value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} />
                      </div>
                      <div className="col-md-3">
                        <input type="date" className="form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="Date de debut" />
                      </div>
                      <div className="col-md-3">
                        <input type="date" className="form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="Date de fin" />
                      </div>
                      <div className="col-md-2">
                        <button type="button" className="btn btn-outline-secondary w-100" onClick={() => { setOrderSearch(""); setDateFrom(""); setDateTo(""); }}>
                          Effacer filtres
                        </button>
                      </div>
                    </div>
                    <small className="text-muted d-block mb-2">{visibleOrders.length} commande(s)</small>
                    <div className="table-responsive">
                      <table className="table mb-0 align-middle">
                        <thead className="table-light text-uppercase" style={{ fontSize: "0.85rem", color: "#1f2937" }}>
                          <tr>
                            <th>Num.</th>
                            <th className="text-center">Boutique</th>
                            <th>Mode</th>
                            <th>État</th>
                            <th>Client</th>
                            <th>Livraison</th>
                            <th className="text-center">Articles</th>
                            <th className="text-end">Total</th>
                            <th className="text-center">CA</th>
                            <th className="text-center">Payé</th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleOrders.map((order) => (
                            <tr key={order.id} role="button" className={selectedId === order.id ? "table-active" : ""} onClick={() => { setSelectedId(order.id); setActiveTab("detail"); }}>
                              <td><span className="text-info fw-medium">{order.orderNumber || order.id}</span></td>
                              <td className="text-center">
                                {order.orderMode === 'in_store' ? (
                                  <i className="bi bi-shop text-secondary" style={{ fontSize: "1.2rem" }} title="En magasin"></i>
                                ) : (
                                  <span style={{ fontSize: "1.2rem", color: "#e67e22", fontWeight: "bold" }}>@</span>
                                )}
                              </td>
                              <td><span className="text-muted">{getOrderModeLabel(order.orderMode)}</span></td>
                              <td><span className={`badge ${getOrderLifecycleBadgeClass(order)}`}>{getOrderLifecycleLabel(order)}</span></td>
                              <td>{order.client}</td>
                              <td className="text-warning fw-bold">{formatOrderDate(order.date)}</td>
                              <td className="text-center">{order.items?.length || 0}</td>
                              <td className="fw-bold text-danger text-end">{formatOrderAmount(order.amount)}</td>
                              <td className="text-center">
                                {order.status !== 'Annulée' && order.status !== 'Annulee' ? (
                                  <div style={{ width: '14px', height: '14px', backgroundColor: '#28a745', display: 'inline-block', borderRadius: '2px' }}></div>
                                ) : (
                                  <div style={{ width: '14px', height: '14px', backgroundColor: '#e9ecef', display: 'inline-block', borderRadius: '2px' }}></div>
                                )}
                              </td>
                              <td className="text-center">
                                {getOrderFinancials(order).paymentStatus === 'Payee' ? (
                                  <div style={{ width: '14px', height: '14px', backgroundColor: '#28a745', display: 'inline-block', borderRadius: '2px' }}></div>
                                ) : (
                                  <div style={{ width: '14px', height: '14px', backgroundColor: '#e9ecef', display: 'inline-block', borderRadius: '2px' }}></div>
                                )}
                              </td>
                            </tr>
                          ))}
                          {!visibleOrders.length ? <tr><td colSpan="10" className="text-muted text-center py-4">Aucune commande.</td></tr> : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                </>
                )}

                {activeTab === "detail" && selectedOrder && (
                  <div className="card border-0 shadow-sm order-detail-panel-container">
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <h4 className="mb-0 d-flex align-items-center">
                          Commande {selectedOrder.orderNumber || selectedOrder.id} 
                          <span className="ms-3" style={{ fontSize: "1.1rem" }}>
                            {selectedOrder.orderMode === 'in_store' ? (
                              <i className="bi bi-shop text-secondary" title="En magasin"></i>
                            ) : (
                              <span style={{ fontSize: "1.2rem", color: "#e67e22", fontWeight: "bold" }}>@</span>
                            )}
                          </span>
                          <span className="text-muted small ms-2" style={{ fontSize: "0.85rem" }}>({selectedOrder.items?.length || 0} ARTICLES)</span>
                        </h4>
                        
                        <div className="d-flex align-items-center gap-3">
                          {(() => {
                            const fin = getOrderFinancials(selectedOrder);
                            if (fin.remainingAmount > 0 && selectedOrder.status !== "Annulée") {
                              return (
                                <button 
                                  className="btn btn-warning fw-bold d-flex align-items-center gap-2"
                                  style={{ color: "#92400e", border: "2px solid #fbbf24" }}
                                  onClick={() => setShowPaymentModal(true)}
                                >
                                  RESTE À PAYER {formatOrderAmount(fin.remainingAmount)}
                                </button>
                              );
                            }
                            return null;
                          })()}

                          <select 
                            className="form-select form-select-sm" 
                            style={{ width: "160px" }}
                            value={selectedOrder.status} 
                            onChange={async (e) => {
                              const newStatus = e.target.value;
                              const nextOrder = { ...selectedOrder, status: newStatus };
                              await updateOrders(orders.map((o) => (o.id === selectedOrder.id ? nextOrder : o)));
                            }}
                          >
                            <option value="Reçue">Reçue</option>
                            <option value="Erreur de paiement">Erreur de paiement</option>
                            <option value="Attente réappro">En attente de réappro</option>
                            <option value="En préparation">En préparation</option>
                            <option value="Prête">Prête</option>
                            <option value="Expédiée">Expédiée</option>
                            <option value="Livrée">Livrée</option>
                            <option value="Annulée">Annulée</option>
                          </select>
                          <div className="order-nav-arrows">
                            <button className="btn btn-sm btn-outline-secondary me-1" onClick={()=>navigateOrder(-1)}>&lt; PREC</button>
                            <button className="btn btn-sm btn-outline-secondary" onClick={()=>navigateOrder(1)}>SUIV &gt;</button>
                          </div>
                        </div>
                      </div>

                      {showPaymentModal && (
                        <B2BPaymentModal 
                          order={selectedOrder}
                          remainingBalance={getOrderFinancials(selectedOrder).remainingAmount}
                          onClose={() => setShowPaymentModal(false)}
                          onApplyPayment={handleApplyPayment}
                        />
                      )}

                      <OrderPipeline order={selectedOrder} lifecycleKey={lifecycleKey} />

                      <div className="row mt-4">
                        <div className="col-lg-8 border-end pe-lg-4">
                          <ul className="list-group list-group-flush">
                            {selectedOrder.items.map((item) => (
                              <li className="list-group-item px-0 py-3" key={`${selectedOrder.id}-${item.id || item.name}`}>
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <div className="fw-bold">{item.name}</div>
                                    <div className="text-muted small">Quantité: {item.quantity} {item.unitType !== 'piece' ? item.unitType : ''}</div>
                                  </div>
                                  <div className="d-flex align-items-center gap-3">
                                    <div className="fw-bold text-success">{formatOrderAmount(item.lineTotal)}</div>
                                    <button 
                                      className="btn btn-sm btn-outline-warning" 
                                      title="Retourner"
                                      onClick={() => handleReturnItem(item)}
                                    >
                                      <i className="fas fa-undo"></i>
                                    </button>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                          <OrderSAV messages={selectedOrder.savMessages} onAddMessage={handleAddSAVMessage} />
                        </div>
                        <div className="col-lg-4 ps-lg-4 bg-light p-3">
                          <div className="mb-3">
                            <span className="badge bg-secondary mb-2">{getOrderModeLabel(selectedOrder.orderMode).toUpperCase()}</span>
                            <div className="fw-bold fs-5">{selectedOrder.client}</div>
                            <div className="text-muted small">{formatOrderDate(selectedOrder.date)}</div>
                          </div>
                          
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted">Total</span>
                            <span className="fw-bold text-primary fs-5">{formatOrderAmount(selectedOrder.amount)}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted small">TVA</span>
                            <span className="text-muted small">{formatOrderAmount((selectedOrder.tax_breakdown?.totalTVA) || 0)}</span>
                          </div>

                          <OrderDocumentBar 
                            order={{...selectedOrder, lifecycleKey}} 
                            onPrintTicket={() => printCashierReceipt(selectedOrder)}
                            onPrintExchange={() => printOrderCreditNote(selectedOrder)}
                            onPrintDelivery={() => printOrderDeliveryNote(selectedOrder)}
                            onPrintBonDeCommande={() => printBonDeCommande(selectedOrder)}
                            onPrintInvoice={() => printOrderInvoice(selectedOrder)}
                            onEmailInvoice={handleEmailInvoice}
                          />

                          {messageTemplates.length > 0 && (
                            <div className="border rounded p-3 mt-4 bg-white">
                              <label className="form-label small fw-semibold mb-2">Envoyer un message prédéfini</label>
                              <div className="d-flex gap-2">
                                <select className="form-select form-select-sm" defaultValue="">
                                  <option value="" disabled>Choisir un message...</option>
                                  {messageTemplates.map((t) => (
                                    <option key={t.id} value={t.id}>{t.nom}</option>
                                  ))}
                                </select>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  title="Envoyer par email"
                                  onClick={(e) => {
                                    const select = e.currentTarget.previousElementSibling;
                                    if (!select.value) { alert("Choisissez d'abord un message prédéfini."); return; }
                                    handleSendTemplateEmail(select.value, selectedOrder);
                                  }}
                                >
                                  <i className="fas fa-envelope"></i> Envoyer
                                </button>
                              </div>
                              <small className="text-muted d-block mt-2">
                                Ouvre votre logiciel de messagerie pré-rempli avec ce message, adressé à {selectedOrder.email || "l'email du client"}.
                              </small>
                            </div>
                          )}

                          <div className="mt-4">
                            <label className="form-label small fw-semibold">Attribuer un livreur</label>
                            <select className="form-select form-select-sm" value={selectedOrder.assignedDriverId || ""} onChange={async (e) => {
                              const driver = driversList.find(d => d.id === e.target.value);
                              await updateOrders(orders.map(o => o.id === selectedOrder.id ? { ...o, assignedDriverId: e.target.value, assignedDriverName: driver ? driver.name : "" } : o));
                            }}>
                              <option value="">-- Aucun --</option>
                              {driversList.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "messages" && (
                  <div className="card border-0 shadow-sm">
                    <div className="card-body p-4">
                      <h4 className="mb-1">Messages prédéfinis</h4>
                      <p className="text-muted mb-4">
                        Créez des messages types envoyés aux clients (ex. retard de livraison).
                      </p>

                      <form onSubmit={handleSaveTemplate} className="border rounded p-3 mb-4 bg-light">
                        <div className="row g-2">
                          <div className="col-md-3">
                            <label className="form-label small fw-semibold mb-1">Nom</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Ex. Retard stock"
                              value={templateForm.nom}
                              onChange={(e) => setTemplateForm({ ...templateForm, nom: e.target.value })}
                              required
                            />
                          </div>
                          <div className="col-md-7">
                            <label className="form-label small fw-semibold mb-1">Message</label>
                            <textarea
                              className="form-control"
                              rows={3}
                              placeholder="Contenu du message envoyé au client"
                              value={templateForm.message}
                              onChange={(e) => setTemplateForm({ ...templateForm, message: e.target.value })}
                              required
                            />
                          </div>
                          <div className="col-md-2 d-flex align-items-end">
                            <div className="d-flex gap-2 w-100">
                              <button type="submit" className="btn btn-primary w-100" style={{ backgroundColor: '#e67e22', borderColor: '#e67e22' }}>
                                {editingTemplateId ? "Enregistrer" : "Ajouter"}
                              </button>
                              {editingTemplateId && (
                                <button type="button" className="btn btn-outline-secondary" onClick={resetTemplateForm}>Annuler</button>
                              )}
                            </div>
                          </div>
                        </div>
                      </form>

                      <div className="table-responsive">
                        <table className="table mb-0 align-middle">
                          <thead className="table-light">
                            <tr><th style={{width: 70}}>ID</th><th>Nom</th><th>Message</th><th style={{width: 130}} className="text-end">Actions</th></tr>
                          </thead>
                          <tbody>
                            {messageTemplates.map((template) => (
                              <tr key={template.id}>
                                <td><span className="badge bg-secondary">{template.id}</span></td>
                                <td className="fw-semibold">{template.nom}</td>
                                <td className="text-muted small">{template.message}</td>
                                <td className="text-end">
                                  <button type="button" className="btn btn-sm btn-outline-primary me-1" title="Modifier" onClick={() => startEditTemplate(template)}>
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button type="button" className="btn btn-sm btn-outline-danger" title="Supprimer" onClick={() => handleDeleteTemplate(template)}>
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {!messageTemplates.length ? (
                              <tr><td colSpan="4" className="text-muted text-center py-4">Aucun message prédéfini.</td></tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default MyAcconutPaymentMethod;
